import type {
  ProgramFixedContentRow,
  ProgramFixedContentRowUpsert,
  ProgramProgressBgColorByField,
  ProgramScopedContentRow,
} from '../types/domain';
import type { ProgramProgressRow } from './programProgressRows';
import { formatLearningTextForDisplay, getUnitTextAndNoteForDisplay } from './programCellDisplayText';
import {
  cellLearningTextForEditor,
  readProgramLearningContentFields,
} from './programLearningContentFields';
export function normalizeFixedEventNameForWorkspace(raw: string): string {
  return (raw ?? '').trim().replace(/^◆+/, '').replace(/-+$/, '').trim();
}

type ProgramFixedUpsertWithSkip = ProgramFixedContentRowUpsert & { _skipFixedTablePersist?: boolean };

/** `_skipFixedTablePersist` 가상 행을 제외하고 DB upsert·digest 비교에 쓸 스냅샷만 남긴다 */
export function filterOutSkipFixedTablePersistRows(
  rows: ProgramFixedContentRowUpsert[]
): ProgramFixedContentRowUpsert[] {
  return rows
    .filter((r) => !(r as ProgramFixedUpsertWithSkip)._skipFixedTablePersist)
    .map((r) => {
      const { _skipFixedTablePersist: _sk, ...rest } = r as ProgramFixedUpsertWithSkip;
      return rest;
    });
}

function stableBgRecord(bg: ProgramProgressBgColorByField | null | undefined): Record<string, string> {
  const src = (bg ?? {}) as Record<string, string>;
  return Object.keys(src)
    .sort()
    .reduce<Record<string, string>>((acc, k) => {
      if (src[k]) acc[k] = src[k];
      return acc;
    }, {});
}

/** id·updatedAt 제외, 행 순서·내용만 비교 (워크스페이스 dirty 판별용) */
export function programFixedWorkspaceRowsDigest(rows: ProgramFixedContentRowUpsert[]): string {
  const filtered = filterOutSkipFixedTablePersistRows(rows);
  const normalized = filtered.map((r) => ({
    learning: readProgramLearningContentFields(r),
    order_index: r.order_index ?? 0,
    source_annual_cell_key: r.source_annual_cell_key ?? null,
    event_name: (r.event_name ?? '').trim() || null,
    timetable_text: r.timetable_text ?? '',
    unit_text: r.unit_text ?? '',
    learning_text: r.learning_text ?? '',
    learning_note: r.learning_note ?? '',
    presentation_ref_id: r.presentation_ref_id ?? null,
    worksheet_ref_id: r.worksheet_ref_id ?? null,
    page_text: r.page_text ?? '',
    assistant_page_text: r.assistant_page_text ?? '',
    current_period_text: r.current_period_text ?? '',
    total_period_text: r.total_period_text ?? '',
    material_text: r.material_text ?? '',
    record_cross_curricular_text: r.record_cross_curricular_text ?? '',
    record_memo_text: r.record_memo_text ?? '',
    record_evaluation_text: r.record_evaluation_text ?? '',
    record_class_text: r.record_class_text ?? '',
    record_member_group_id: r.record_member_group_id ?? null,
    bg: stableBgRecord(r.bg_color_by_field),
  }));
  normalized.sort((a, b) => {
    if (a.order_index !== b.order_index) return a.order_index - b.order_index;
    return String(a.source_annual_cell_key ?? '').localeCompare(String(b.source_annual_cell_key ?? ''));
  });
  return JSON.stringify(normalized);
}

export function fixedDbRowsToWorkspaceUpserts(rows: ProgramFixedContentRow[]): ProgramFixedContentRowUpsert[] {
  return [...rows]
    .sort((a, b) => a.order_index - b.order_index)
    .map((r) => ({
      id: r.id?.startsWith('virtual-') ? undefined : r.id,
      workspace_id: r.workspace_id,
      grade_id: r.grade_id,
      academic_year: r.academic_year,
      term_number: r.term_number,
      order_index: r.order_index,
      source_annual_cell_key: r.source_annual_cell_key ?? null,
      event_name: r.event_name ?? null,
      timetable_text: r.timetable_text ?? '',
      unit_text: r.unit_text ?? '',
      learning_text: cellLearningTextForEditor(r),
      learning_note: r.learning_note ?? '',
      presentation_ref_id: r.presentation_ref_id ?? null,
      worksheet_ref_id: r.worksheet_ref_id ?? null,
      page_text: r.page_text ?? '',
      assistant_page_text: r.assistant_page_text ?? '',
      current_period_text: r.current_period_text ?? '',
      total_period_text: r.total_period_text ?? '',
      material_text: r.material_text ?? '',
      record_cross_curricular_text: r.record_cross_curricular_text ?? '',
      record_memo_text: r.record_memo_text ?? '',
      record_evaluation_text: r.record_evaluation_text ?? '',
      record_class_text: r.record_class_text ?? '',
      record_member_group_id: r.record_member_group_id ?? null,
      bg_color_by_field: { ...(r.bg_color_by_field ?? {}) },
    }));
}

/** 편제 프로그램: 서버 scoped + 일정행 기준 upsert 스냅샷(워크스페이스 없을 때 표 초기값) */
export function buildTrackFixedWorkspaceBaselineUpserts(params: {
  workspaceId: string;
  academicYear: number;
  termNumber: 1 | 2;
  gradeId: string;
  filteredRows: ProgramProgressRow[];
  scopedRowsSorted: ProgramScopedContentRow[];
}): ProgramFixedContentRowUpsert[] {
  const { workspaceId, academicYear, termNumber, gradeId, filteredRows, scopedRowsSorted } = params;
  const out: ProgramFixedContentRowUpsert[] = [];
  filteredRows.forEach((row, orderIndex) => {
    const contentRow = scopedRowsSorted[orderIndex];
    const unitText = contentRow?.unit_text ?? '';
    const learningFields = readProgramLearningContentFields(contentRow);
    const eventNameRaw =
      row.memo ||
      row.fixedEventName ||
      getUnitTextAndNoteForDisplay(unitText).text ||
      learningFields.body ||
      '';
    const eventName = normalizeFixedEventNameForWorkspace(eventNameRaw) || null;
    const recordClassText = contentRow?.record_class_text ?? '';
    const bg: Record<string, string> = { ...((contentRow?.bg_color_by_field ?? {}) as Record<string, string>) };
    const cid = contentRow?.id ?? '';
    out.push({
      id: cid.startsWith('virtual-') ? undefined : cid || undefined,
      workspace_id: workspaceId,
      grade_id: gradeId,
      academic_year: academicYear,
      term_number: termNumber,
      /** 편제·프로젝트: 표 위치 = 순서(일정 i번째 ↔ 내용 i번째) */
      order_index: orderIndex,
      source_annual_cell_key: row.annualCellKey,
      event_name: eventName,
      timetable_text: '',
      unit_text: unitText,
      learning_text: cellLearningTextForEditor(contentRow),
      learning_note: learningFields.note,
      presentation_ref_id: learningFields.presentationRefId,
      worksheet_ref_id: learningFields.worksheetRefId,
      page_text: contentRow?.page_text ?? '',
      assistant_page_text: contentRow?.assistant_page_text ?? '',
      current_period_text: contentRow?.current_period_text ?? '',
      total_period_text: contentRow?.total_period_text ?? '',
      material_text: contentRow?.material_text ?? '',
      record_cross_curricular_text: contentRow?.record_cross_curricular_text ?? '',
      record_memo_text: contentRow?.record_memo_text ?? '',
      record_evaluation_text: contentRow?.record_evaluation_text ?? '',
      record_class_text: recordClassText,
      record_member_group_id: contentRow?.record_member_group_id ?? null,
      bg_color_by_field: bg,
    });
  });
  return out;
}

/** 프로젝트 프로그램: 서버 scoped 정렬본 기준 스냅샷 */
export function buildProjectFixedWorkspaceBaselineUpserts(params: {
  workspaceId: string;
  academicYear: number;
  termNumber: 1 | 2;
  gradeId: string;
  rows: ProgramProgressRow[];
  scopedRowsSorted: ProgramScopedContentRow[];
}): ProgramFixedContentRowUpsert[] {
  const { workspaceId, academicYear, termNumber, gradeId, rows, scopedRowsSorted } = params;
  const out: ProgramFixedContentRowUpsert[] = [];
  rows.forEach((row, orderIndex) => {
    const contentRow = scopedRowsSorted[orderIndex];
    const unitText = contentRow?.unit_text ?? '';
    const learningFields = readProgramLearningContentFields(contentRow);
    const eventNameRaw =
      row.memo ||
      row.fixedEventName ||
      getUnitTextAndNoteForDisplay(unitText).text ||
      learningFields.body ||
      '';
    const eventName = normalizeFixedEventNameForWorkspace(eventNameRaw) || null;
    const cid = contentRow?.id ?? '';
    out.push({
      id: cid.startsWith('virtual-') ? undefined : cid || undefined,
      workspace_id: workspaceId,
      grade_id: gradeId,
      academic_year: academicYear,
      term_number: termNumber,
      /** 편제·프로젝트: 표 위치 = 순서(일정 i번째 ↔ 내용 i번째) */
      order_index: orderIndex,
      source_annual_cell_key: row.annualCellKey,
      event_name: eventName,
      /**
       * 프로젝트 표의 시간표 열은 프로그램 내용이 권위.
       * 내용 행이 없거나 빈칸이면 연간 셀 text로 채우지 않는다(삭제 후 빈칸 유지·내보내기 반영).
       */
      timetable_text: contentRow?.timetable_text ?? '',
      unit_text: unitText,
      learning_text: cellLearningTextForEditor(contentRow),
      learning_note: learningFields.note,
      presentation_ref_id: learningFields.presentationRefId,
      worksheet_ref_id: learningFields.worksheetRefId,
      page_text: contentRow?.page_text ?? '',
      assistant_page_text: contentRow?.assistant_page_text ?? '',
      current_period_text: contentRow?.current_period_text ?? '',
      total_period_text: contentRow?.total_period_text ?? '',
      material_text: contentRow?.material_text ?? '',
      record_cross_curricular_text: contentRow?.record_cross_curricular_text ?? '',
      record_memo_text: contentRow?.record_memo_text ?? '',
      record_evaluation_text: contentRow?.record_evaluation_text ?? '',
      record_class_text: contentRow?.record_class_text ?? '',
      record_member_group_id: contentRow?.record_member_group_id ?? null,
      bg_color_by_field: { ...(contentRow?.bg_color_by_field ?? {}) },
    });
  });
  return out;
}

type AlignableProgramContentRow = Pick<
  ProgramFixedContentRowUpsert,
  | 'id'
  | 'workspace_id'
  | 'grade_id'
  | 'academic_year'
  | 'term_number'
  | 'order_index'
  | 'source_annual_cell_key'
  | 'event_name'
  | 'timetable_text'
  | 'unit_text'
  | 'learning_text'
  | 'learning_note'
  | 'presentation_ref_id'
  | 'worksheet_ref_id'
  | 'page_text'
  | 'assistant_page_text'
  | 'current_period_text'
  | 'total_period_text'
  | 'material_text'
  | 'record_cross_curricular_text'
  | 'record_memo_text'
  | 'record_evaluation_text'
  | 'record_class_text'
  | 'record_member_group_id'
  | 'bg_color_by_field'
> & {
  created_at?: string;
  updated_at?: string;
};

/**
 * 프로그램 표는 스케줄과 내용 중 더 긴 쪽을 기준으로 표시한다.
 * - 스케줄만 있는 인덱스: baseline의 공란 프로그램 행을 유지해 저장 가능한 빈 내용으로 만든다.
 * - 내용만 있는 인덱스: 스케줄 source를 null로 두어 UI에서는 일정 칸만 비운 overflow 행으로 표시한다.
 * - 프로그램 내용은 화면의 위→아래 행 순서 자체가 원본이므로, 완전 공란 행도 의도된 빈 행으로 유지한다.
 */
export function alignProgramContentRowsToScheduleSlotLayout(params: {
  contentRowsSorted: readonly AlignableProgramContentRow[];
  baselineRows: readonly ProgramFixedContentRowUpsert[];
}): ProgramFixedContentRowUpsert[] {
  const { contentRowsSorted, baselineRows } = params;
  const contentRows = [...contentRowsSorted];
  const total = Math.max(contentRows.length, baselineRows.length);
  const out: ProgramFixedContentRowUpsert[] = [];
  for (let i = 0; i < total; i += 1) {
    const content = contentRows[i];
    const baseline = baselineRows[i];
    if (content) {
      const sourceKey = baseline ? baseline.source_annual_cell_key ?? null : null;
      const learningFields = readProgramLearningContentFields(content);
      out.push({
        ...(baseline ?? {}),
        ...content,
        id: content.id,
        order_index: i,
        source_annual_cell_key: sourceKey,
        event_name: baseline?.event_name ?? content.event_name ?? null,
        timetable_text: content.timetable_text ?? baseline?.timetable_text ?? '',
        unit_text: content.unit_text ?? '',
        learning_text: cellLearningTextForEditor(content),
        learning_note: learningFields.note,
        presentation_ref_id: learningFields.presentationRefId,
        worksheet_ref_id: learningFields.worksheetRefId,
        page_text: content.page_text ?? '',
        assistant_page_text: content.assistant_page_text ?? '',
        current_period_text: content.current_period_text ?? '',
        total_period_text: content.total_period_text ?? '',
        material_text: content.material_text ?? '',
        record_cross_curricular_text: content.record_cross_curricular_text ?? '',
        record_memo_text: content.record_memo_text ?? '',
        record_evaluation_text: content.record_evaluation_text ?? '',
        record_class_text: content.record_class_text ?? '',
        record_member_group_id: content.record_member_group_id ?? null,
        bg_color_by_field: { ...(content.bg_color_by_field ?? {}) },
      });
    } else if (baseline) {
      out.push({
        ...baseline,
        id: baseline.id?.startsWith('virtual-') ? undefined : baseline.id,
        order_index: i,
      });
    }
  }
  return out;
}

type ProgramContentRowPresenceFields = Pick<
  ProgramFixedContentRowUpsert,
  | 'unit_text'
  | 'learning_text'
  | 'page_text'
  | 'assistant_page_text'
  | 'current_period_text'
  | 'total_period_text'
  | 'material_text'
  | 'record_cross_curricular_text'
  | 'record_memo_text'
  | 'record_evaluation_text'
  | 'record_class_text'
  | 'timetable_text'
  | 'event_name'
  | 'presentation_ref_id'
  | 'worksheet_ref_id'
  | 'record_member_group_id'
  | 'bg_color_by_field'
>;

/** 단원·학습내용·쪽수·기록 등 사용자가 입력한 프로그램 내용이 있는지 */
export function programContentRowHasMeaningfulData(row: ProgramContentRowPresenceFields): boolean {
  const { text, note } = getUnitTextAndNoteForDisplay(String(row.unit_text ?? ''));
  if (text.trim() || note.trim()) return true;
  if (formatLearningTextForDisplay(String(row.learning_text ?? '')).trim()) return true;
  if (String(row.page_text ?? '').trim()) return true;
  if (String(row.assistant_page_text ?? '').trim()) return true;
  if (String(row.current_period_text ?? '').trim()) return true;
  if (String(row.total_period_text ?? '').trim()) return true;
  if (String(row.material_text ?? '').trim()) return true;
  if (String(row.record_cross_curricular_text ?? '').trim()) return true;
  if (String(row.record_memo_text ?? '').trim()) return true;
  if (String(row.record_evaluation_text ?? '').trim()) return true;
  if (String(row.record_class_text ?? '').trim()) return true;
  if (String(row.timetable_text ?? '').trim()) return true;
  if (String(row.event_name ?? '').trim()) return true;
  if (row.presentation_ref_id) return true;
  if (row.worksheet_ref_id) return true;
  if (row.record_member_group_id) return true;
  const bg = row.bg_color_by_field ?? {};
  if (Object.values(bg).some((v) => (v ?? '').trim() !== '')) return true;
  return false;
}

/** `source_annual_cell_key`가 있는 마지막 일정 슬롯 행까지의 최소 행 수(넘침 orphan 제외 기준선). */
export function inferProgramContentScheduleSlotRowCount(
  rows: readonly Pick<ProgramFixedContentRowUpsert, 'source_annual_cell_key'>[]
): number {
  let maxScheduledIdx = -1;
  for (let i = 0; i < rows.length; i += 1) {
    const key = rows[i].source_annual_cell_key;
    if (key != null && String(key).trim() !== '') maxScheduledIdx = i;
  }
  return maxScheduledIdx + 1;
}

/**
 * 마지막 실제 데이터 이후의 빈 넘침(orphan) 행을 제거한다.
 * 일정 슬롯 수만큼의 행은 내용이 비어 있어도 유지한다.
 * 일정·데이터가 모두 없어도 표에 입력할 수 있도록 기본 최소 1행을 남긴다.
 * `temp-` id 빈 행은 «수업 추가»로 막 넣은 자리이므로 dirty 여부와 관계없이 유지한다.
 */
export function trimTrailingEmptyProgramContentOverflowRows<
  T extends ProgramFixedContentRowUpsert & Pick<ProgramFixedContentRowUpsert, 'source_annual_cell_key'>,
>(rows: readonly T[], options?: { minRowCount?: number }): T[] {
  if (!rows.length) return [];
  const floor = Math.max(options?.minRowCount ?? 1, inferProgramContentScheduleSlotRowCount(rows));
  let end = rows.length;
  while (end > floor) {
    const row = rows[end - 1];
    if (programContentRowHasMeaningfulData(row)) break;
    const id = String(row.id ?? '');
    if (id.startsWith('temp-')) break;
    end -= 1;
  }
  if (end === rows.length) return [...rows];
  return rows.slice(0, end) as T[];
}

/** 표에 깔아 둘 최소 1행(일정·DB 정렬 결과가 0행일 때). `virtual-` id는 저장 시 신규 행으로 upsert됨 */
export const MIN_DISPLAY_PROGRAM_CONTENT_ROW_ID = 'virtual-cp-min-program-content-row';

/**
 * 일정·DB가 비었을 때만 쓰는 UI용 빈 1행 — 내용이 비어 있는 상태.
 * 이 스냅샷이 `persistKind: replace`로 LS 워크스페이스에 남으면, 이후 서버 baseline이 길어져도
 * `mergeProgramWorkspaceUpsertsIntoBaselineRows`가 행 수를 워크스페이스 길이로 줄여 표 전체가 1행으로 망가진다.
 */
export function isPristineMinimumProgramContentPlaceholderRows(
  rows: readonly Pick<
    ProgramFixedContentRowUpsert,
    | 'id'
    | 'timetable_text'
    | 'unit_text'
    | 'learning_text'
    | 'page_text'
    | 'assistant_page_text'
    | 'current_period_text'
    | 'total_period_text'
    | 'material_text'
    | 'record_cross_curricular_text'
    | 'record_memo_text'
    | 'record_evaluation_text'
    | 'record_class_text'
    | 'event_name'
    | 'source_annual_cell_key'
  >[]
): boolean {
  if (rows.length !== 1) return false;
  const r = rows[0];
  if ((r.id ?? '') !== MIN_DISPLAY_PROGRAM_CONTENT_ROW_ID) return false;
  const texts = [
    r.timetable_text,
    r.unit_text,
    r.learning_text,
    r.page_text,
    r.assistant_page_text,
    r.current_period_text,
    r.total_period_text,
    r.material_text,
    r.record_cross_curricular_text,
    r.record_memo_text,
    r.record_evaluation_text,
    r.record_class_text,
    r.event_name,
  ];
  if (texts.some((t) => (t ?? '').trim() !== '')) return false;
  if (r.source_annual_cell_key != null && String(r.source_annual_cell_key).trim() !== '') return false;
  return true;
}

/**
 * 정렬된 표 행이 0개일 때 UI용 빈 1행을 붙인다.
 * 편제별/프로젝트 프로그램 페이지에서는 **연간 일정 슬롯이 없을 때만** 호출해,
 * 일정이 생기면 같은 순서로 맞춰 넣을 수 있게 미리 편집할 수 있게 한다.
 */
export function withMinimumProgramContentRowsForDisplay(
  aligned: ProgramFixedContentRowUpsert[],
  params: {
    workspaceId: string;
    gradeId: string;
    academicYear: number;
    termNumber: 1 | 2;
  }
): ProgramFixedContentRowUpsert[] {
  if (aligned.length > 0) return aligned;
  const { workspaceId, gradeId, academicYear, termNumber } = params;
  return [
    {
      id: MIN_DISPLAY_PROGRAM_CONTENT_ROW_ID,
      workspace_id: workspaceId,
      grade_id: gradeId,
      academic_year: academicYear,
      term_number: termNumber,
      order_index: 0,
      source_annual_cell_key: null,
      event_name: null,
      timetable_text: '',
      unit_text: '',
      learning_text: '',
      learning_note: '',
      presentation_ref_id: null,
      worksheet_ref_id: null,
      page_text: '',
      assistant_page_text: '',
      current_period_text: '',
      total_period_text: '',
      material_text: '',
      record_cross_curricular_text: '',
      record_memo_text: '',
      record_evaluation_text: '',
      record_class_text: '',
      bg_color_by_field: {},
    },
  ];
}
